import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import "../css/ManageBrands.css";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import {
  ref,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { db, storage } from "../firebase";
import { CacheManager, CACHE_KEYS } from "../utils/cache";
import { convertToWebP } from "../utils/imageConverter";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth } from "../firebase";
import toast from "react-hot-toast";

// صفحة إدارة العلامات التجارية
function ManageBrands() {
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [formData, setFormData] = useState({
    id: null,
    name: "",
    description: "",
    logo: "",
    website: "",
    country: "",
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const [oldLogoUrl, setOldLogoUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState(""); // Add search functionality
  const [currentPage, setCurrentPage] = useState(1); // Add pagination
  const [itemsPerPage] = useState(6); // 6 brands per page

  useEffect(() => {
    async function fetchBrands() {
      try {
        const snapshot = await getDocs(collection(db, "brands"));
        const data = [];
        snapshot.forEach((doc) => data.push({ id: doc.id, ...doc.data() }));
        setBrands(data);
      } catch (error) {
        // بيانات تجريبية
        setBrands([
          {
            id: "brand1",
            name: "لوريال",
            description: "علامة تجارية فرنسية للعناية بالشعر",
            logo: "",
            website: "https://loreal.com",
            country: "فرنسا",
          },
          {
            id: "brand2",
            name: "بانتين",
            description: "منتجات العناية بالشعر من بروكتر آند جامبل",
            logo: "",
            website: "https://pantene.com",
            country: "أمريكا",
          },
          {
            id: "brand3",
            name: "هيد آند شولدرز",
            description: "شامبو ضد القشرة",
            logo: "",
            website: "https://headandshoulders.com",
            country: "أمريكا",
          },
        ]);
      }
    }
    fetchBrands();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("الرجاء اختيار صورة");
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error("حجم الصورة يجب أن يكون أقل من 5 ميجابايت");
        return;
      }
      setLogoFile(file);
      const previewUrl = URL.createObjectURL(file);
      setLogoPreview(previewUrl);
    }
  };

  const handleRemoveLogo = () => {
    setLogoFile(null);
    setLogoPreview("");
    if (!formData.id) {
      setFormData((prev) => ({ ...prev, logo: "" }));
    }
  };

  const uploadLogo = async (brandId) => {
    if (!logoFile) return formData.logo;

    setUploading(true);
    try {
      // Convert to WebP
      let fileToUpload;
      try {
        fileToUpload = await convertToWebP(logoFile);
      } catch (error) {
        console.error("Error converting logo to WebP:", error);
        fileToUpload = logoFile;
      }

      const fileName = `brands/${brandId}/${Date.now()}_${fileToUpload.name}`;
      const storageRef = ref(storage, fileName);
      const snapshot = await uploadBytes(storageRef, fileToUpload);
      const downloadURL = await getDownloadURL(snapshot.ref);

      // Delete old logo if it exists
      if (oldLogoUrl && oldLogoUrl.includes("firebase")) {
        try {
          // Extract the path from the URL
          const decodedUrl = decodeURIComponent(oldLogoUrl);
          const pathMatch = decodedUrl.match(/\/o\/(.+?)(\?|$)/);

          if (pathMatch && pathMatch[1]) {
            const imagePath = pathMatch[1];
            const oldLogoRef = ref(storage, imagePath);
            await deleteObject(oldLogoRef);
          }
        } catch (error) {
          console.error("Error deleting old logo:", error);
        }
      }

      return downloadURL;
    } catch (error) {
      console.error("Error uploading logo:", error);
      throw error;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      let logoUrl = formData.logo;
      let updatedBrands;

      if (formData.id) {
        // Update existing brand
        if (logoFile) {
          logoUrl = await uploadLogo(formData.id);
        }

        const data = {
          name: formData.name,
          description: formData.description,
          logo: logoUrl,
          website: formData.website,
          country: formData.country,
        };

        const docRef = doc(db, "brands", formData.id);
        await updateDoc(docRef, data);
        updatedBrands = brands.map((b) =>
          b.id === formData.id ? { id: b.id, ...data } : b,
        );
      } else {
        // Add new brand
        const tempData = {
          name: formData.name,
          description: formData.description,
          logo: "",
          website: formData.website,
          country: formData.country,
        };

        const docRef = await addDoc(collection(db, "brands"), tempData);
        const brandId = docRef.id;

        if (logoFile) {
          logoUrl = await uploadLogo(brandId);
          await updateDoc(docRef, { logo: logoUrl });
        }

        updatedBrands = [
          ...brands,
          { id: brandId, ...tempData, logo: logoUrl },
        ];
      }

      setBrands(updatedBrands);

      // Clear customer-facing cache so changes appear immediately
      CacheManager.remove(CACHE_KEYS.BRANDS);

      // Reset form
      setFormData({
        id: null,
        name: "",
        description: "",
        logo: "",
        website: "",
        country: "",
      });
      setLogoFile(null);
      setLogoPreview("");
      setOldLogoUrl("");
      setShowForm(false);

      toast.success(
        formData.id
          ? "تم تحديث العلامة التجارية بنجاح"
          : "تم إضافة العلامة التجارية بنجاح",
      );
    } catch (error) {
      toast.error("حدث خطأ. يرجى المحاولة مرة أخرى.");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (brand) => {
    setFormData({
      id: brand.id,
      name: brand.name,
      description: brand.description || "",
      logo: brand.logo || "",
      website: brand.website || "",
      country: brand.country || "",
    });
    setLogoPreview(brand.logo || "");
    setOldLogoUrl(brand.logo || "");
    setLogoFile(null);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    const confirmDelete = await new Promise((resolve) => {
      const toastId = toast(
        (t) => (
          <div style={{ textAlign: "center" }}>
            <p style={{ marginBottom: "15px", fontWeight: "bold" }}>
              هل تريد حذف هذه العلامة التجارية؟
            </p>
            <div
              style={{ display: "flex", gap: "10px", justifyContent: "center" }}
            >
              <button
                onClick={() => {
                  toast.dismiss(t.id);
                  resolve(true);
                }}
                style={{
                  padding: "8px 20px",
                  background: "#ef4444",
                  color: "white",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                }}
              >
                حذف
              </button>
              <button
                onClick={() => {
                  toast.dismiss(t.id);
                  resolve(false);
                }}
                style={{
                  padding: "8px 20px",
                  background: "#6b7280",
                  color: "white",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                }}
              >
                إلغاء
              </button>
            </div>
          </div>
        ),
        { duration: Infinity },
      );
    });
    if (!confirmDelete) return;
    try {
      await deleteDoc(doc(db, "brands", id));
      const updatedBrands = brands.filter((b) => b.id !== id);
      setBrands(updatedBrands);

      // Clear customer-facing cache so changes appear immediately
      CacheManager.remove(CACHE_KEYS.BRANDS);

      toast.success("تم حذف العلامة التجارية بنجاح");
    } catch (error) {
      toast.error("حدث خطأ أثناء الحذف");
    }
  };

  const handleCancel = () => {
    setFormData({
      id: null,
      name: "",
      description: "",
      logo: "",
      website: "",
      country: "",
    });
    setLogoFile(null);
    setLogoPreview("");
    setOldLogoUrl("");
    setShowForm(false);
  };

  // Filter brands based on search term
  const filteredBrands = searchTerm
    ? brands.filter(
        (brand) =>
          brand.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          brand.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          brand.country?.toLowerCase().includes(searchTerm.toLowerCase()),
      )
    : brands;

  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentBrands = filteredBrands.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredBrands.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Reset to first page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  return (
    <>
      <Navbar />
      <div className="manage-brands-page">
        <h1>إدارة العلامات التجارية</h1>

        {/* Add Brand Button */}
        {!showForm && (
          <div className="mb-add-section">
            <button className="mb-add-button" onClick={() => setShowForm(true)}>
              + إضافة علامة تجارية جديدة
            </button>
          </div>
        )}

        {/* Search Section */}
        {!showForm && (
          <div className="mb-search-bar">
            <input
              type="text"
              placeholder="ابحث عن علامة تجارية بالاسم، الوصف، أو بلد المنشأ..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="mb-search-input"
            />
            <div className="mb-brands-count">
              عرض {indexOfFirstItem + 1}-
              {Math.min(indexOfLastItem, filteredBrands.length)} من{" "}
              {filteredBrands.length} علامة تجارية
            </div>
          </div>
        )}

        {/* Brand Form */}
        {showForm && (
          <form onSubmit={handleSubmit} className="mb-form">
            <h2>
              {formData.id
                ? "تعديل العلامة التجارية"
                : "إضافة علامة تجارية جديدة"}
            </h2>

            <div className="mb-form-group">
              <label>اسم العلامة التجارية:</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                required
                onChange={handleChange}
                placeholder="مثال: لوريال"
              />
            </div>

            <div className="mb-form-group">
              <label>الوصف:</label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                placeholder="وصف مختصر عن العلامة التجارية"
              />
            </div>

            <div className="mb-form-group">
              <label>شعار العلامة التجارية:</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoSelect}
                style={{ display: "none" }}
                id="logo-upload"
              />
              <label htmlFor="logo-upload" className="mb-upload-btn">
                <i className="fas fa-upload"></i>
                {logoPreview ? "تغيير الشعار" : "اختر صورة الشعار"}
              </label>
              {logoPreview && (
                <div className="mb-logo-preview">
                  <img src={logoPreview} alt="Logo preview" />
                  <button
                    type="button"
                    className="mb-remove-logo"
                    onClick={handleRemoveLogo}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              )}
            </div>

            <div className="mb-form-group">
              <label>الموقع الرسمي:</label>
              <input
                type="url"
                name="website"
                value={formData.website}
                onChange={handleChange}
                placeholder="https://example.com"
              />
            </div>

            <div className="mb-form-group">
              <label>بلد المنشأ:</label>
              <input
                type="text"
                name="country"
                value={formData.country}
                onChange={handleChange}
                placeholder="مثال: فرنسا"
              />
            </div>

            <button type="submit" className="mb-save-btn" disabled={loading}>
              {loading ? "جاري الحفظ..." : formData.id ? "تحديث" : "إضافة"}
            </button>
            <button
              type="button"
              className="mb-cancel-btn"
              onClick={handleCancel}
            >
              إلغاء
            </button>
          </form>
        )}

        {/* Brands Table */}
        <table className="mb-table">
          <thead>
            <tr>
              <th>الشعار</th>
              <th>الاسم</th>
              <th>الوصف</th>
              <th>بلد المنشأ</th>
              <th>الموقع</th>
              <th>إجراءات</th>
            </tr>
          </thead>
          <tbody>
            {currentBrands.map((brand) => (
              <tr key={brand.id}>
                <td data-label="الشعار">
                  {brand.logo ? (
                    <img
                      src={brand.logo}
                      alt={brand.name}
                      className="mb-brand-logo"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  ) : (
                    <div className="mb-no-logo">
                      <i className="fas fa-tag"></i>
                    </div>
                  )}
                </td>
                <td data-label="الاسم" className="mb-brand-name">
                  {brand.name}
                </td>
                <td data-label="الوصف" className="mb-brand-description">
                  {brand.description || "لا يوجد وصف"}
                </td>
                <td data-label="بلد المنشأ">{brand.country || "-"}</td>
                <td data-label="الموقع">
                  {brand.website ? (
                    <a
                      href={brand.website}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mb-website-link"
                    >
                      زيارة الموقع
                    </a>
                  ) : (
                    "-"
                  )}
                </td>
                <td data-label="إجراءات">
                  <button
                    className="mb-edit-btn"
                    onClick={() => handleEdit(brand)}
                  >
                    تعديل
                  </button>
                  <button
                    className="mb-delete-btn"
                    onClick={() => handleDelete(brand.id)}
                  >
                    حذف
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        {totalPages > 1 && !showForm && (
          <div className="mb-pagination">
            <button
              className="mb-pagination-btn"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            >
              السابق
            </button>

            {[...Array(totalPages)].map((_, index) => {
              const pageNumber = index + 1;
              return (
                <button
                  key={pageNumber}
                  className={`mb-pagination-btn ${
                    currentPage === pageNumber ? "active" : ""
                  }`}
                  onClick={() => handlePageChange(pageNumber)}
                >
                  {pageNumber}
                </button>
              );
            })}

            <button
              className="mb-pagination-btn"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            >
              التالي
            </button>
          </div>
        )}

        {filteredBrands.length === 0 && !showForm && (
          <div className="mb-no-results">
            <p>لا توجد علامات تجارية {searchTerm ? "تطابق البحث" : ""}</p>
          </div>
        )}
      </div>
    </>
  );
}

export default ManageBrands;
